/**
 * «Envíalo por email» en el navegador (27-09-2026, A21): lo que hace la tarjeta
 * «Enviar por email» del asistente al pulsar «Enviar».
 *
 *  - Lee la tarjeta que dibujó `propose_email` (`leerTarjetaEmail`: lo que no
 *    tiene forma de tarjeta no se pinta).
 *  - Informe del chat: busca en la conversación el informe (el último, o el de
 *    su `report_id`) y lo pinta con LO MISMO que sus botones —el constructor de
 *    /reports para el PDF (`buildReportPdfDoc`) y `buildReportXlsx` para el
 *    Excel—, así que se manda el mismo documento que se descarga.
 *  - Llama a la edge `send-report-email` con la sesión de la persona. La edge
 *    lo comprueba todo otra vez (empresa, rol, alcance, empresa interna, tope);
 *    aquí solo se avisa antes de lo evidente.
 */
import type { AssistantDocSpec } from "@/hooks/useAssistantStream";
import { buildReportPdfDoc } from "@/lib/pdf/buildReportPdf";
import { PdfReportBuilderV2 } from "@/lib/pdf/PdfReportBuilderV2";
import { buildExportFilename } from "@/components/export/useExportPdf";
import { buildReportXlsx, xlsxFilename } from "@/lib/reportXlsx";
import { CODIGOS_ERROR_ENVIO, esUuid, type FormatoEnvio } from "./reportEmailRules";
import type { MarcaInformes } from "@/lib/marca/tipos";
import { prefijoFichero } from "@/lib/marca/fichero";

export type AdjuntoTarjeta =
  | { type: "report"; report_id?: string; report_title?: string; format: FormatoEnvio }
  | {
      type: "cuaderno";
      export_id: string;
      year: number;
      version: number;
      format: FormatoEnvio;
      generated_at?: string;
      titular_location_id?: string | null;
    };

export interface TarjetaEmail {
  send_id: string;
  to: string[];
  subject: string;
  message: string;
  attachment: AdjuntoTarjeta;
}

/** La tarjeta tal y como la dibujó `propose_email` (o null si no tiene forma de tarjeta). */
export function leerTarjetaEmail(raw: Record<string, unknown> | null | undefined): TarjetaEmail | null {
  if (!raw || typeof raw !== "object") return null;
  if (!esUuid(raw.send_id)) return null;
  const to = Array.isArray(raw.to) ? raw.to.filter((x): x is string => typeof x === "string") : [];
  const a = raw.attachment as Record<string, unknown> | undefined;
  if (!a || typeof a !== "object") return null;
  const format: FormatoEnvio = a.format === "xlsx" ? "xlsx" : "pdf";
  let attachment: AdjuntoTarjeta;
  if (a.type === "report") {
    attachment = {
      type: "report",
      format,
      ...(typeof a.report_id === "string" ? { report_id: a.report_id } : {}),
      ...(typeof a.report_title === "string" && a.report_title.trim() ? { report_title: a.report_title.trim().slice(0, 120) } : {}),
    };
  } else if (a.type === "cuaderno" && esUuid(a.export_id)) {
    attachment = {
      type: "cuaderno",
      export_id: a.export_id,
      year: Number(a.year),
      version: Number(a.version),
      format,
      generated_at: typeof a.generated_at === "string" ? a.generated_at : undefined,
      titular_location_id: typeof a.titular_location_id === "string" ? a.titular_location_id : null,
    };
  } else return null;
  return {
    send_id: raw.send_id,
    to,
    subject: typeof raw.subject === "string" ? raw.subject : "",
    message: typeof raw.message === "string" ? raw.message : "",
    attachment,
  };
}

/** Los informes de la conversación que aún se pueden mandar, del más nuevo al más viejo. */
export function informesDisponibles(docs: AssistantDocSpec[]): AssistantDocSpec[] {
  return docs.filter((d) => !d.unavailable && Array.isArray(d.rows)).reverse();
}

const clave = (s: unknown) =>
  String(s ?? "").normalize("NFD").replace(/\p{M}+/gu, "").toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").trim();

/**
 * El informe que se manda (28-09-2026: «que elija cuál, o que sea el único; por
 * defecto, el último»):
 *   1. el que nombró la persona por su TÍTULO («el del inventario de Campohermoso»);
 *   2. el último de ese tipo (`report_id`);
 *   3. si no, el ÚLTIMO de la conversación. Antes, pedir «el de stock» cuando el
 *      último era uno compuesto dejaba la tarjeta en «no hay ningún informe».
 * null solo si en la conversación no hay ninguno que se pueda descargar.
 */
export function buscarInforme(docs: AssistantDocSpec[], reportId?: string, titulo?: string): AssistantDocSpec | null {
  const hay = informesDisponibles(docs);
  if (!hay.length) return null;
  const pedido = clave(titulo);
  if (pedido) {
    const palabras = pedido.split(" ").filter((w) => w.length >= 3);
    const porTitulo = hay.find((d) => {
      const tt = clave(d.title);
      return tt.includes(pedido) || (palabras.length > 0 && palabras.every((w) => tt.includes(w)));
    });
    if (porTitulo) return porTitulo;
  }
  if (reportId) {
    const porTipo = hay.find((d) => d.report_id === reportId);
    if (porTipo) return porTipo;
  }
  return hay[0];
}

/** bytes → base64, por trozos (un spread de megas revienta la pila). */
export function aBase64(bytes: Uint8Array): string {
  let s = "";
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

export interface DepsFichero {
  companyName: string;
  /** La marca de la empresa (Personalizar informes): el adjunto sale igual que la descarga. */
  marca: MarcaInformes | null;
  language: string;
  t: (k: string) => string;
  formatCurrency: (n: number) => string;
}

/** El PDF o el Excel del informe, con los mismos constructores que los botones de su tarjeta. */
export async function ficheroDelInforme(doc: AssistantDocSpec, formato: FormatoEnvio, deps: DepsFichero): Promise<{ bytes: Uint8Array; fileName: string }> {
  if (formato === "xlsx") {
    const blob = await buildReportXlsx(doc, deps.companyName, deps.marca);
    return { bytes: new Uint8Array(await blob.arrayBuffer()), fileName: xlsxFilename(doc, prefijoFichero(deps.marca)) };
  }
  const pdf = await buildReportPdfDoc({
    orientation: doc.orientation ?? "portrait",
    marca: deps.marca,
    companyName: deps.companyName,
    reportSubtitle: doc.title,
    dateRange: doc.date_start && doc.date_end ? PdfReportBuilderV2.formatDateRange(doc.date_start, doc.date_end, deps.language) : undefined,
    filters: doc.filters,
    kpis: doc.kpis,
    columns: doc.columns,
    rows: doc.rows,
    sections: doc.sections,
    formatCurrency: deps.formatCurrency,
    footer: {
      generated: `${deps.t("pdfExport.generatedOn")} ${new Date().toLocaleString(deps.language, { dateStyle: "medium", timeStyle: "short" })}`,
      page: deps.t("pdfExport.page"),
      of: deps.t("pdfExport.of"),
      madeWith: deps.t("marcaInformes.pdf.madeWith"),
    },
  });
  return { bytes: new Uint8Array(pdf.output("arraybuffer")), fileName: buildExportFilename(doc.report_id, doc.date_start, doc.date_end, prefijoFichero(deps.marca)) };
}

export type ResultadoEnvio =
  | { ok: true; status: "sent" | "simulated"; recipients: string[]; attached: boolean; already?: boolean }
  | { ok: false; code: string; recipients?: string[] };

/** Lo que devolvió la edge, bueno o malo, en una forma. Un código que no conocemos es «error». */
export async function leerRespuestaEnvio(data: unknown, error: unknown): Promise<ResultadoEnvio> {
  let body: Record<string, unknown> | null = data && typeof data === "object" ? (data as Record<string, unknown>) : null;
  if (error) {
    body = null;
    const ctx = (error as { context?: { json?: () => Promise<unknown> } })?.context;
    if (ctx && typeof ctx.json === "function") {
      try {
        const j = await ctx.json();
        if (j && typeof j === "object") body = j as Record<string, unknown>;
      } catch {
        /* sin cuerpo legible */
      }
    }
    if (!body) return { ok: false, code: "EMAIL_RED" };
  }
  if (body?.ok === true && (body.status === "sent" || body.status === "simulated")) {
    return {
      ok: true,
      status: body.status,
      recipients: Array.isArray(body.recipients) ? (body.recipients as string[]) : [],
      attached: body.attached === true,
      ...(body.already === true ? { already: true } : {}),
    };
  }
  const code = typeof body?.code === "string" && (CODIGOS_ERROR_ENVIO as readonly string[]).includes(body.code) ? body.code : "EMAIL_ERROR";
  return { ok: false, code, ...(Array.isArray(body?.recipients) ? { recipients: body!.recipients as string[] } : {}) };
}
